import { getApiClient } from '../axios-config';

/**
 * Areas API Service
 * Handles all API calls related to areas
 */
class AreasAPI {
  /**
   * Get areas for a project
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of area objects
   */
  static async getByProject(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/areas/getareas', {
      params: { projectId },
    });
    return response.data.data.areas || [];
  }

  /**
   * Get leaf-level areas for a project
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Array>} Array of leaf-level area objects
   */
  static async getLeafAreas(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/areas', {
      params: { projectId, leafOnly: true },
    });
    return response.data.data.areas || [];
  }

  /**
   * Create a new area
   * @param {Object} area - Area object
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Created area object
   */
  static async create(area, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/areas/addarea', {
      newArea: area,
    });
    const { addedArea, accCategorySync } = response.data.data;
    // accCategorySync reports whether the area's ACC category was set up (#257).
    return addedArea ? { ...addedArea, accCategorySync } : area;
  }

  /**
   * Update an existing area
   * @param {Object} area - Area object with updates
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated area object
   */
  static async update(area, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.put('/api/areas/modifyarea', {
      updatedArea: area,
    });
    return response.data.data.modifiedArea || area;
  }

  /**
   * Delete an area
   * @param {string} id - Area ID to delete
   * @param {string} projectId - Project ID (required)
   * @param {string} token - JWT authentication token
   * @returns {Promise<void>}
   */
  static async delete(id, projectId, token) {
    const apiClient = getApiClient(token);
    await apiClient.delete('/api/areas/deletearea', {
      params: {
        deletedAreaId: id,
        projectId: projectId,
      },
    });
  }
}

export default AreasAPI;
